/**
 * Store: Moment maker
 * -----------------------------------------------------------------------------
 * Moment maker steps state.
 * - Only JSDOC custom parameters, ignore module state or object.
 *
 */
export default {
  namespaced: true,

  state: () => ({
    activePersona: 0,
    currentStep: 0,
    clickableSteps: 0,
    product: null,
    personas: [],
  }),

  mutations: {

    /**
     * Set a step's attribute
     * @param {string} key - Attribute name
     * @param {any} value - Value to save
     */
    SET_ATTRIBUTE(state, { key, value }) {
      state[key] = value
    },

    /**
     * Set gift message
     * @param {string} key - Message key
     * @param {string} message - Message to save
     */
    SET_GIFT_MESSAGE_CACHE(state, { key, message }) {
      const persona = state.personas[state.activePersona]
      if (!persona) {
        return
      }
      persona.gift.cache[key] = message
    },

    /**
     * Add a new persona, limit to 3 personas, and update active persona to
     * the latest persona
     * If a persona name exists already, that persona will be removed and the
     * new persona will be added.
     * @param {object} persona
     */
    SET_PERSONA(state, persona) {
      const oldPersonas = state.personas.filter(
        oldPersona => oldPersona.name !== persona.name,
      ).slice(-2)

      state.personas = [
        ...oldPersonas,
        persona,
      ]

      state.activePersona = state.personas.length
        ? state.personas.length - 1
        : 0
    },

    /**
     * Set a step's attribute
     * @param {string} key - Attribute name
     * @param {string} step - The step name
     * @param {any} value - Value to save
     */
    SET_PERSONA_ATTRIBUTE(state, { step, key, value }) {
      const persona = state.personas[state.activePersona]
      if (!persona || !persona[step]) {
        return
      }
      persona[step][key] = value
    },
  },

  actions: {

    /**
     * Cache gift message using tone and characteristics
     * @param {string} key - Message key to use for cache
     * @param {string} message - Message to cache
     */
    cacheGiftMessage({ commit }, { key, message }) {
      commit('SET_GIFT_MESSAGE_CACHE', { key, message })
    },

    /**
     * Create a persona
     * @param {string} name - Persona name from API
     * @param {string} description - Persona description from API
     */
    createPersona({ commit }, { name, description }) {
      commit('SET_PERSONA', {
        name,
        description,
        intro: {},
        traits: {
          values: [],
        },
        shopping: {
          selectedCollectionId: null,
          selectedProduct: null,
          descriptions: {},
          collectionIds: [],
        },
        gift: {
          tone: '',
          characteristics: '',
          cache: {},
          cartMessage: '',
        },
      })
    },

    /**
     * Save attribute to store
     * @param {string} key - Attribute name
     * @param {any} value - Value to save
     */
    saveAttribute({ commit }, { key, value }) {
      commit('SET_ATTRIBUTE', { key, value })
    },

    /**
     * Save attribute to step
     * @param {string} key - Attribute name
     * @param {string} step - The step name
     * @param {any} value - Value to save
     */
    savePersonaAttribute({ commit }, { step, key, value }) {
      commit('SET_PERSONA_ATTRIBUTE', { step, key, value })
    },
  },

  getters: {

    /**
     * Get active persona settings using the persona name.
     * @returns {object}
     */
    getActivePersona(state) {
      return state.personas[state.activePersona]
    },

    /**
     * Get the currently cached gift message using the current
     * tone and characteristics
     * @returns {string}
     */
    getCachedMessage(state, getters) {
      const step = getters.getActivePersona.gift
      const messageKey = step.tone + step.characteristics
      return step.cache[messageKey] || ''
    },

    /**
     * Get the number of saved personas
     * @returns {number}
     */
    getNumSavedPersonas(state) {
      return state.personas.length
    },
  },
}
